/*
 * Cablea los eventos de authClient al store de Redux y arranca la sesión.
 * Es el único sitio de la UI que escucha a authClient: revoked/guest cierran
 * la sesión por forceLogout (utils/auth-session.js), session desde otra
 * pestaña recarga la página (limpia react-query, zustand y Redux de una vez),
 * session conseguida por esta misma pestaña fuera de un thunk se adopta sin
 * recargar, unavailable solo avisa.
 */
import { message as mensajeAntd, notification } from 'antd';
import { forceLogout } from 'utils/auth-session';
import { adoptarSesion, bootTerminado } from 'store/slices/authSlice';
import authClient, { CODIGOS_DE_SESION } from './authClient';
import { mensajeDeAuth, mensajeDeError } from './authMessages';

const EVENTOS = ['session', 'guest', 'revoked', 'unavailable'];

function conDefectos(store, deps) {
  return {
    cliente: authClient,
    recargar: () => window.location.reload(),
    avisar: (texto) => notification.warning({ message: texto }),
    avisarBreve: (texto) =>
      mensajeAntd.warning({
        content: texto,
        key: 'sesion-no-disponible',
        duration: 4,
      }),
    cerrar: forceLogout,
    adoptar: () => store.dispatch(adoptarSesion()).unwrap(),
    ...deps,
  };
}

export function conectar(store, deps = {}) {
  const d = conDefectos(store, deps);
  const auth = () => store.getState().auth;
  const oyentes = {
    // Otra pestaña entró mientras esta estaba en el login (`deAfuera`):
    // recargar deja la app limpia. Si la sesión la consiguió ESTA pestaña
    // fuera de un thunk —/auth/enrolar llama a authClient.activate() directo,
    // o el reintento en frío tras un boot `unavailable` acaba trayendo
    // sesión— recargar tiraría la pantalla debajo del usuario: se adopta y
    // ya. Durante un login propio (loading) o el boot no aplica: el thunk o
    // arrancar() adoptan la sesión.
    session: (sesion, info) => {
      const a = auth();
      if (a.token || a.booting || a.loading) return;
      if (info && info.deAfuera) d.recargar();
      else
        d.adoptar().catch((e) => {
          // Nadie más avisa: authSlice no registra `adoptarSesion.rejected`,
          // así que sin esto el rechazo sería mudo y el usuario se quedaría en
          // el login con la sesión viva detrás. El token no se toca: cerrarlo
          // por un fallo de /session es justo lo que se estaba arreglando.
          d.avisarBreve(mensajeDeError(e));
        });
    },
    guest: () => {
      if (auth().token) d.cerrar();
    },
    revoked: (code, message) => {
      d.avisar(mensajeDeAuth({ code, message }));
      d.cerrar();
    },
    unavailable: () => {
      d.avisarBreve(mensajeDeAuth({ code: 'SESSION_UNAVAILABLE' }));
    },
  };
  // authClient.on(evento, fn) devuelve la función para darse de baja: no hay
  // off(), así que desconectar() llama a las bajas que guardamos aquí.
  const bajas = EVENTOS.map((e) => d.cliente.on(e, oyentes[e]));
  return () => bajas.forEach((baja) => baja());
}

// Códigos con los que la sesión ya no existe del lado del server: son los
// únicos que justifican tirar lo que hay. Cualquier otro fallo (red, 5xx,
// SESSION_UNAVAILABLE) es transitorio.
const CIERRAN_SESION = [...CODIGOS_DE_SESION, 'REFRESH_INVALID'];

// boot() antes de pintar nada: con cookie válida adopta la sesión; como
// invitado o sin servicio, la app arranca en el login (authClient sigue
// reintentando por detrás y, si consigue sesión, el oyente de arriba la adopta).
export async function arrancar(store, deps = {}) {
  const d = conDefectos(store, deps);
  const parar = conectar(store, d);
  try {
    const resultado = await d.cliente.boot();
    if (resultado === 'session') await d.adoptar();
  } catch (e) {
    // boot() ya devolvió 'session', o sea que el refresco trajo un token bueno:
    // lo único que pudo fallar aquí es /session (503, red) o el localStorage de
    // Safari privado. Cerrar por eso dejaría la UI de invitado con una sesión
    // viva detrás y obligaría a un login que no hace falta. Se cierra en dos
    // casos: cuando el código dice que la sesión ya no existe, y cuando después
    // del fallo no queda token en memoria —authClient pudo limpiarla él mismo
    // (un /session 401 TOKEN_EXPIRED cuyo refresco forzado devuelve
    // REFRESH_INVALID propaga el error ORIGINAL, así que el código no delata el
    // cierre) y entonces no hay nada que conservar—. Si no, se avisa y el token
    // queda (la app arranca en el login y el siguiente intento lo aprovecha).
    if (CIERRAN_SESION.includes(e && e.code) || !d.cliente.getAccessToken())
      await d.cerrar();
    else d.avisarBreve(mensajeDeError(e));
  } finally {
    store.dispatch(bootTerminado());
  }
  return parar;
}
